import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { APIDecoratorWithBaseURI } from '../../Service/api'
import { AxiosError, AxiosResponse } from 'axios'
import { useState, useEffect, useCallback } from 'react'

export interface interfaceObject {
  description: string
  procedureReference: string
  rawValue: number
  formatedValue: string
  type: string
}
export interface entrieProps {
  procedureReference: string
  value: number | string
}
export interface modeledDataProps {
  creditTotal: {
    formatedTotal: string
    rawTotal: number
  }
  credit: interfaceObject[]
  totalGMV: string
  eventName: string
  statusFechamento: string
  usuarioProprietarioId: number
}
export interface FinancialCloseResponseProps {
  success: boolean
  data: modeledDataProps
}

export interface CreditProps {
  creditData: null | interfaceObject[]
  creditFormatedTotal: string
  usuarioProprietarioId: number | null
  creditRawTotal: number
  financialState: string
  eventName: string
  financialGMV: string
  isLoading: boolean
  isError: undefined | AxiosError
}
export interface UseFinancialCloseProps {
  financialData: AxiosResponse<FinancialCloseResponseProps> | undefined
  isLoading: boolean
  isError: AxiosError
  mutate: () => Promise<AxiosResponse<FinancialCloseResponseProps> | undefined>
}

interface ApproveRejectResponse {
  success: boolean
}

interface UseApproveRejectProps {
  trigger: (
    action: 'approve' | 'reject'
  ) => Promise<AxiosResponse<ApproveRejectResponse> | undefined>
  isMutating: boolean
  error: string | undefined
}

export const useSWRRequest = (eventId: number): UseFinancialCloseProps => {
  const url: string = `producer/analytics/finacialClose?eventId=${eventId}`

  const fetchData = async (): Promise<AxiosResponse> => {
    return await APIDecoratorWithBaseURI().get(url)
  }

  const { data, error, isLoading, mutate } = useSWR(url, fetchData, {
    refreshInterval: 600000,
  })

  return {
    financialData: data,
    isLoading,
    isError: error,
    mutate
  }
}

export const useFinancialClosing = (eventId: number): {
  credit: CreditProps
  mutate: () => Promise<AxiosResponse<FinancialCloseResponseProps> | undefined>
} => {
  const [credit, setCredit] = useState<CreditProps>({
    creditData: null,
    creditFormatedTotal: '',
    creditRawTotal: 0,
    financialState: '',
    eventName: '',
    financialGMV: '',
    isLoading: true,
    usuarioProprietarioId: null,
    isError: undefined,
  })

  const { financialData, isLoading, isError, mutate } = useSWRRequest(eventId)

  useEffect(() => {
    if (financialData !== undefined) {
      const creditRaw = financialData.data.data
      setCredit({
        creditData: creditRaw.credit,
        creditFormatedTotal: creditRaw.creditTotal.formatedTotal,
        creditRawTotal: creditRaw.creditTotal.rawTotal,
        financialState: creditRaw.statusFechamento,
        financialGMV: creditRaw.totalGMV,
        eventName: creditRaw.eventName,
        usuarioProprietarioId: creditRaw.usuarioProprietarioId,
        isLoading: isLoading,
        isError: isError,
      })
    }
  }, [financialData, isLoading, isError])

  return { credit, mutate }
}

const sendApproveReject = async (
  url: string,
  { arg: action }: { arg: 'approve' | 'reject' }
): Promise<AxiosResponse<ApproveRejectResponse>> => {
  return await APIDecoratorWithBaseURI().post(url + `/${action}`)
}

export const useApproveReject = (eventId: number): UseApproveRejectProps => {
  const url = `/producer/analytics/approve-reject-financial-close/${eventId}`

  const { trigger, error, isMutating } = useSWRMutation(url, sendApproveReject)

  const triggerApproveReject = useCallback(
    async (
      action: 'approve' | 'reject'
    ): Promise<AxiosResponse<ApproveRejectResponse> | undefined> => {
      return await trigger(action)
    },
    [trigger]
  )

  return {
    trigger: triggerApproveReject,
    isMutating,
    error: error?.response?.data?.message as string | undefined,
  }
}
